import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { qk, type AgentThread } from '@immoaktas/api-client';
import { AGENT_FILE_ACCEPT, AGENT_LIMITS, agentSection, type AgentFeedbackRating, type AgentMessageDto, type AgentProposalDto, type AgentTip } from '@immoaktas/shared';
import { api, useAuth } from '../lib/api';
import { useNotify } from '../lib/hooks';
import { Button, ConfirmDialog, Icon, IconButton, Spinner, type IconName } from '../ui';
import { FileChip } from './FileChip';
import { PremiumCard } from './PremiumCard';
import { Markdown } from './Markdown';
import { ProposalCard } from './ProposalCard';
import { AwayCard, CaseCard, Suggestions, starterSection, useText } from './Guide';
import { useBusySteps, useReveal } from './typing';
import { LONG_CHAT_MESSAGES, useAgent, useAgentHistory, useAgentUi } from './useAgent';
import './agent.css';

/**
 * KI-Assistent panel on the right (docs/17 §Oberfläche, design like the Hirezard coach): "KI" label (AI Act Art. 50),
 * tips for the page (no AI cost), the steering of the Sachbearbeiter (page-aware greeting, case card with checklist
 * and the proposals waiting for it, next-step chips with deep links, "Während du weg warst"), the chat with proposal
 * cards and questions with answer chips, conversation starters per page and a composer that takes files by button,
 * drag & drop or paste. Shows consent, outage and limit states, the Premium offer with the trial counter (the only
 * place that sells Premium) and the remaining messages of the month. Tips, page hints and the case card can be hidden
 * for this chat (×), the next chat shows them again; suggestions above the composer stay one row and leave out what
 * the case card or a tip already offers. The composer stays open without Premium: "Senden"
 * then shows the offer (or the limit note) instead of calling the API. History of earlier chats like in the chat with
 * Claude (docs/17 §Verlauf): a new session starts empty, "Verlauf" lists the chats, a click continues one.
 */
export function AgentPanel({ mobile }: { mobile: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useNotify();
  const me = useAuth((s) => s.me);
  const a = useAgent();
  const { ui, status, thread } = a;
  // while the panel is open, "Übernehmen" on a card of this chat continues the case by itself (docs/adr/0013)
  const continueAfter = a.continueAfter;
  useEffect(() => {
    useAgentUi.getState().set({ afterApply: (p) => void continueAfter(p) });
    return () => useAgentUi.getState().set({ afterApply: null });
  }, [continueAfter]);
  const [text, setText] = useState('');
  const [dragging, setDragging] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [consentBusy, setConsentBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const [allTips, setAllTips] = useState(false);
  const drag = useRef(0);
  const scroll = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const messages = thread.data?.messages ?? [];
  const proposals = new Map((thread.data?.proposals ?? []).map((p) => [p.id, p]));
  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
  const question = lastAssistant?.question && !ui.busy ? lastAssistant : null;
  const s = status.data;
  const tx = useText();
  const guide = a.guide.data ?? null;
  // pending proposals belong to the case of this chat: shown grouped in the case card instead of between messages
  const grouped = !!guide?.case;
  const pending = grouped ? (thread.data?.proposals ?? []).filter((p) => p.status === 'pending' || p.status === 'failed') : [];
  const pendingIds = new Set(pending.map((p) => p.id));
  const newest = lastAssistant && messages.at(-1)?.id === lastAssistant.id ? lastAssistant.id : null;
  const busyStep = useBusySteps(ui.busy);
  const history = useAgentHistory(a.ready && ui.open);
  const chats = (history.data?.pages ?? []).flatMap((p) => p.items);
  const recent = chats.filter((c) => c.id !== ui.conversationId).slice(0, 3);

  useEffect(() => {
    scroll.current?.scrollTo?.({ top: scroll.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, ui.busy, ui.outgoing?.id]);
  useEffect(() => {
    if (ui.open && !mobile) input.current?.focus();
  }, [ui.open, mobile]);

  const texts = { tooLarge: (n: string) => t('agent.tooLarge', { name: n }), badType: (n: string) => t('agent.badType', { name: n }) };
  const take = (files: FileList | File[] | null | undefined) => {
    if (!files?.length) return;
    const left = a.attach(Array.from(files), texts);
    setNote(left > 0 ? t('agent.tooMany', { max: AGENT_LIMITS.filesPerMessage }) : null);
    // without Premium the offer shows right away; the files stay attached for after the purchase
    a.checkAccess();
    requestAnimationFrame(() => input.current?.focus());
  };
  const sendable = (text.trim().length > 0 || ui.attached.some((x) => !x.error)) && !ui.busy && a.canSend;
  const access = s?.access;
  const budget = s?.budget;
  /** Sends the composer text, a chip text (`override`) or an answer to the assistant's question. */
  const submit = (override?: string, answer?: { messageId: string; choice: string; skip?: boolean }) => {
    const v = answer ? '' : (override ?? text).trim();
    if (!answer && !v && !ui.attached.some((x) => !x.error)) return;
    if (ui.busy || !a.canSend) return;
    // no Premium or budget used up: show why instead of sending, the typed text stays in the composer
    if (!a.checkAccess()) return;
    setView('chat');
    if (override === undefined) setText('');
    setNote(null);
    a.send(v, answer ? { answer } : {})
      .then((sent) => {
        if (!sent && override === undefined && !answer) setText((cur) => cur || v);
      })
      .catch((e) => toast.err(e));
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (question && text.trim()) submit(undefined, { messageId: question.id, choice: text.trim() });
      else submit();
    }
  };
  const consent = async () => {
    setConsentBusy(true);
    try {
      await api.agent.grantConsent();
      await qc.invalidateQueries({ queryKey: qk.agent.status });
    } catch (e) {
      toast.err(e);
    } finally {
      setConsentBusy(false);
    }
  };
  const deleteChat = async () => {
    const id = confirmDelete;
    setConfirmDelete(null);
    if (!id) return;
    try {
      await api.agent.deleteConversation(id);
      if (id === ui.conversationId) a.newChat();
      await qc.invalidateQueries({ queryKey: qk.agent.conversations });
    } catch (e) {
      toast.err(e);
    }
  };
  const open = (id: string) => {
    a.openChat(id);
    setView('chat');
  };
  const fresh = () => {
    a.newChat();
    setView('chat');
    requestAnimationFrame(() => input.current?.focus());
  };

  const tips = (a.tips.data ?? []).filter((x) => !ui.hiddenTips.includes(x.id));
  const name = me?.user.name?.split(' ')[0];
  const seen = async (id: string) => {
    try {
      await api.agent.briefingSeen(id);
    } catch {
      /* shown again next time */
    }
    void qc.invalidateQueries({ queryKey: ['agent', 'guide'] });
  };
  const ask = (q: string) => submit(q);
  const awayItems = guide?.away?.items.filter((x) => !ui.hiddenTips.includes(x.id)) ?? [];
  const away = guide?.away && awayItems.length ? { ...guide.away, items: awayItems } : null;
  const canAsk = a.canSend && !ui.busy;
  const caseId = guide?.case ? `case:${guide.case.id}` : null;
  const showCase = !!guide?.case && !ui.hiddenTips.includes(caseId!);
  const shownTips = tips.slice(0, allTips ? tips.length : TIPS_SHOWN);
  // a suggestion that only repeats the case card or a tip on screen is left out
  const onScreen = new Set([...shownTips, ...(away?.items ?? [])].map((x) => `tip:${x.id}`));
  const actions = !question && guide ? guide.actions.filter((x) => !(showCase && x.id.startsWith('case:')) && !onScreen.has(x.id)) : [];
  const starters = !messages.length ? starterSection(a.page.path, agentSection(a.page.path)) : null;

  return (
    <aside
      className={`agent-panel${mobile ? ' mobile' : ''}${dragging ? ' dragging' : ''}`}
      aria-label={t('agent.name')}
      onDragEnter={(e) => {
        if (!e.dataTransfer?.types?.includes('Files') || !a.canSend) return;
        e.preventDefault();
        drag.current++;
        setDragging(true);
      }}
      onDragOver={(e) => {
        if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
      }}
      onDragLeave={() => {
        drag.current = Math.max(0, drag.current - 1);
        if (!drag.current) setDragging(false);
      }}
      onDrop={(e) => {
        if (!e.dataTransfer?.files?.length) return;
        e.preventDefault();
        drag.current = 0;
        setDragging(false);
        take(e.dataTransfer.files);
      }}
    >
      <header className="agent-head">
        <span className="agent-mark" aria-hidden>{t('agent.badge')}</span>
        <div className="agent-title">
          <strong>{t('agent.name')}</strong>
          <span>{t('agent.claim')}</span>
        </div>
        {a.ready && <IconButton icon="history" label={view === 'history' ? t('agent.history.back') : t('agent.history.title')} size="sm" onClick={() => setView(view === 'history' ? 'chat' : 'history')} />}
        {a.ready && (messages.length > 0 || view === 'history') && <IconButton icon="plus" label={t('agent.newChat')} size="sm" onClick={fresh} />}
        {a.ready && ui.conversationId && view === 'chat' && <IconButton icon="trash" label={t('agent.deleteChat')} size="sm" onClick={() => setConfirmDelete(ui.conversationId)} />}
        <IconButton icon="close" label={t('agent.close')} size="sm" onClick={() => ui.setOpen(false)} />
      </header>

      {a.ready && view === 'history' ? (
        <div className="agent-body agent-history">
          <div className="agent-history-head">
            <strong>{t('agent.history.title')}</strong>
            <Button size="sm" variant="secondary" onClick={fresh}>{t('agent.newChat')}</Button>
          </div>
          {history.isPending && <Spinner />}
          {history.isSuccess && !chats.length && <p className="agent-notice">{t('agent.history.empty')}</p>}
          <ChatList items={chats} current={ui.conversationId} onOpen={open} onDelete={(id) => setConfirmDelete(id)} />
          {history.hasNextPage && (
            <Button size="sm" variant="ghost" loading={history.isFetchingNextPage} onClick={() => void history.fetchNextPage()}>{t('agent.history.more')}</Button>
          )}
        </div>
      ) : (
      <div className="agent-body" ref={scroll} aria-live="polite">
        {status.isPending && <Spinner />}
        {s && !s.configured && <p className="agent-notice">{t('agent.notConfigured')}</p>}
        {s && s.configured && !s.enabled && <p className="agent-notice">{t('agent.disabled')}</p>}
        {s && s.configured && s.enabled && !s.consent.granted && (
          <section className="agent-consent">
            <strong>{t('agent.consent.title')}</strong>
            <p>{t('agent.consent.intro')}</p>
            <ul>
              <li>{t('agent.consent.what')}</li>
              <li>{t('agent.consent.who')}</li>
              <li>{t('agent.consent.why')}</li>
              <li>{t('agent.consent.training')}</li>
            </ul>
            <p className="small">{t('agent.consent.choice')}</p>
            <div className="agent-row">
              <Button size="sm" loading={consentBusy} onClick={() => void consent()}>{t('agent.consent.accept')}</Button>
              <Button size="sm" variant="ghost" onClick={() => ui.setOpen(false)}>{t('agent.consent.later')}</Button>
            </div>
          </section>
        )}
        {s?.consent.granted && !s.canWrite && <p className="agent-notice">{t('agent.readOnly')}</p>}
        {a.ready && access?.via === 'trial' && <PremiumCard access={access} />}
        {a.ready && access?.via && budget?.exhausted && <p className="agent-notice" role="status">{t(budget.exhausted === 'month' ? 'agent.limitMonth' : 'agent.limit')}</p>}

        {a.ready && !messages.length && !ui.outgoing && (guide ? <p className="agent-intro">{[name ? t('agent.hello', { name }) : '', tx(guide.greeting)].filter(Boolean).join(' ')}</p> : <p className="agent-intro">{name ? t('agent.intro', { name }) : t('agent.introNoName')}</p>)}
        {a.ready && away && <AwayCard b={away} renderTip={(tip) => <Tip tip={tip} ready={canAsk} onAsk={ask} onHide={() => ui.hideTip(tip.id)} />} onSeen={() => void seen(away.id)} />}
        {a.ready && guide?.case && showCase && <CaseCard c={guide.case} pending={pending} ready={a.canSend} busy={ui.busy} onAsk={ask} onHide={() => ui.hideTip(caseId!)} />}

        {tips.length > 0 && (
          <section className="agent-tips" aria-label={t('agent.tipsTitle')}>
            {shownTips.map((tip) => <Tip key={tip.id} tip={tip} ready={canAsk} onAsk={ask} onHide={() => ui.hideTip(tip.id)} />)}
            {tips.length > TIPS_SHOWN && (
              <button type="button" className="agent-more" aria-expanded={allTips} onClick={() => setAllTips(!allTips)}>
                {allTips ? t('agent.fewerTips') : t('agent.moreTips', { count: tips.length - TIPS_SHOWN })}
                <Icon name="chevronDown" size={14} />
              </button>
            )}
          </section>
        )}

        {a.ready && !messages.length && !ui.outgoing && !ui.conversationId && recent.length > 0 && (
          <section className="agent-recent" aria-label={t('agent.history.recent')}>
            <div className="agent-history-head">
              <strong>{t('agent.history.recent')}</strong>
              <button type="button" className="link" onClick={() => setView('history')}>{t('agent.history.all')}</button>
            </div>
            <ChatList items={recent} current={null} onOpen={open} />
          </section>
        )}
        {a.ready && messages.length > LONG_CHAT_MESSAGES && (
          <div className="agent-long" role="note">
            <p>{t('agent.longChat')}</p>
            <div className="agent-row">
              <Button size="sm" variant="secondary" onClick={fresh}>{t('agent.newChat')}</Button>
            </div>
          </div>
        )}
        {messages.map((m) => <Message key={m.id} m={m} proposals={proposals} busy={ui.busy} skip={pendingIds} reveal={m.id === newest} />)}
        {question?.question && (
          <div className="agent-question">
            {question.question.why && (
              <details>
                <summary>{t('agent.why')}</summary>
                <p>{question.question.why}</p>
              </details>
            )}
            <div className="agent-chips">
              {question.question.options.map((o) => (
                <button key={o} type="button" className="agent-qchip" disabled={ui.busy} onClick={() => submit(o, { messageId: question.id, choice: o })}>{o}</button>
              ))}
              <button type="button" className="agent-qchip ghost" disabled={ui.busy} onClick={() => submit(t('agent.skip'), { messageId: question.id, choice: t('agent.skip'), skip: true })}>{t('agent.skip')}</button>
            </div>
            {question.question.allowFree && <small>{t('agent.freeAnswer')}</small>}
          </div>
        )}
        {ui.outgoing && (
          <div className="agent-out">
            {ui.outgoing.files.length > 0 && (
              <ul className="agent-chips-list sent">
                {ui.outgoing.files.map((f) => <li key={f.id}><FileChip name={f.name} sizeBytes={f.size} phase={f.phase} error={f.error ?? null} /></li>)}
              </ul>
            )}
            {ui.outgoing.text && <div className="agent-msg me">{ui.outgoing.text}</div>}
            {ui.outgoing.failed && <small className="agent-failed">{t('agent.file.failed')}</small>}
          </div>
        )}
        {ui.busy && (
          <div className="agent-busy" role="status">
            <span className="agent-dots" aria-hidden><i /><i /><i /></span>
            {t(`agent.busySteps.${busyStep}`)}
          </div>
        )}
      </div>
      )}

      <footer className="agent-foot">
        {a.canSend && !text.trim() && <Suggestions actions={actions} starters={starters} ready={a.canSend} busy={ui.busy} onAsk={ask} />}
        {ui.blocker && (
          <div className="agent-blocker" role="alert">
            {ui.blocker === 'premium' && access ? <PremiumCard access={{ ...access, via: null }} required /> : <p className="agent-notice">{t(ui.blocker === 'limit_month' ? 'agent.limitMonth' : 'agent.limit')}</p>}
            <button type="button" className="link muted" onClick={() => ui.set({ blocker: null })}>{t('common:close')}</button>
          </div>
        )}
        {note && (
          <p className="agent-note" role="alert">
            {note}
            <button type="button" className="link" onClick={() => setNote(null)}>{t('common:close')}</button>
          </p>
        )}
        <form
          className="agent-ask"
          onSubmit={(e) => {
            e.preventDefault();
            if (question && text.trim()) submit(undefined, { messageId: question.id, choice: text.trim() });
            else submit();
          }}
        >
          {ui.attached.length > 0 && (
            <ul className="agent-chips-list">
              {ui.attached.map((x) => (
                <li key={x.id}><FileChip name={x.file.name} sizeBytes={x.file.size} error={x.error} onRemove={() => ui.set({ attached: ui.attached.filter((y) => y.id !== x.id) })} /></li>
              ))}
            </ul>
          )}
          <div className="agent-ask-row">
            <IconButton icon="upload" label={t('agent.attach')} size="sm" disabled={!a.canSend || ui.attached.length >= AGENT_LIMITS.filesPerMessage} onClick={() => fileInput.current?.click()} />
            <input ref={fileInput} type="file" className="sr-only" accept={AGENT_FILE_ACCEPT} tabIndex={-1} aria-hidden multiple onChange={(e) => { take(e.target.files); e.target.value = ''; }} />
            <textarea
              ref={input}
              rows={1}
              value={text}
              maxLength={AGENT_LIMITS.textMax}
              disabled={!a.canSend}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKey}
              onPaste={(e) => {
                const files = Array.from(e.clipboardData?.files ?? []);
                if (!files.length) return;
                e.preventDefault();
                take(files);
              }}
              placeholder={question ? t('agent.answerPlaceholder') : t('agent.placeholder')}
              aria-label={question ? t('agent.answerPlaceholder') : t('agent.placeholder')}
            />
            <button type="submit" className="agent-send" disabled={!sendable && !(question && text.trim())} aria-label={t('agent.send')}>
              <Icon name="arrowRight" size={16} />
            </button>
          </div>
        </form>
        {a.ready && access?.via === 'trial' && <p className="agent-ainote">{t('agent.trialLeft', { count: access.trialLeft, total: access.trialTotal })}</p>}
        {a.ready && (access?.via === 'premium' || access?.via === 'override') && budget && !budget.exhausted && <p className="agent-ainote">{t('agent.budgetMonth', { count: budget.messagesLeft })}</p>}
        <p className="agent-ainote">{t('agent.aiNote')}</p>
      </footer>
      {dragging && (
        <div className="agent-drop" aria-hidden>
          <strong>{t('agent.drop')}</strong>
          <span>{t('agent.dropSub')}</span>
        </div>
      )}
      <ConfirmDialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={() => void deleteChat()} title={t('agent.deleteChat')} text={t('agent.deleteChatText')} label={t('agent.deleteChat')} danger />
    </aside>
  );
}

/** Tips shown before "n weitere Hinweise"; the rest opens on demand. */
const TIPS_SHOWN = 3;

/** "vor 3 Tagen", "gestern", "heute"; older than a week: the date. */
function useRelative() {
  const { i18n } = useTranslation();
  return (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const days = Math.round((day(new Date()) - day(d)) / 86400_000);
    if (days >= 0 && days < 7) return new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' }).format(-days, 'day');
    return new Intl.DateTimeFormat(i18n.language, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
  };
}

function ChatList({ items, current, onOpen, onDelete }: { items: { id: string; title: string; lastMessageAt: string }[]; current: string | null; onOpen: (id: string) => void; onDelete?: (id: string) => void }) {
  const { t } = useTranslation();
  const rel = useRelative();
  if (!items.length) return null;
  return (
    <ul className="agent-chatlist">
      {items.map((c) => (
        <li key={c.id} className={c.id === current ? 'current' : undefined}>
          <button type="button" className="agent-chatlist-open" aria-current={c.id === current ? 'true' : undefined} onClick={() => onOpen(c.id)}>
            <span className="agent-chatlist-title">{c.title || t('agent.history.untitled')}</span>
            <time dateTime={c.lastMessageAt}>{rel(c.lastMessageAt)}</time>
          </button>
          {onDelete && <IconButton icon="trash" label={t('agent.history.delete', { title: c.title || t('agent.history.untitled') })} size="sm" onClick={() => onDelete(c.id)} />}
        </li>
      ))}
    </ul>
  );
}

function Message({ m, proposals, busy, skip, reveal }: { m: AgentMessageDto; proposals: Map<string, AgentProposalDto>; busy: boolean; skip: Set<string>; reveal: boolean }) {
  const { t } = useTranslation();
  const shown = useReveal(m.role === 'assistant' ? m.text : '', reveal, m.id);
  // written by the app after "Übernehmen": a quiet line, the assistant then goes on with the next step
  if (m.role === 'user' && m.kind === 'continue') return <p className="agent-continue">{t('agent.continued', { title: m.text.replace(/^Übernommen: /, '') })}</p>;
  if (m.role === 'user')
    return (
      <div className="agent-out">
        {m.attachments.length > 0 && (
          <ul className="agent-chips-list sent">
            {m.attachments.map((f) => <li key={f.id}><FileChip name={f.name} sizeBytes={f.size} pages={f.pages ?? null} /></li>)}
          </ul>
        )}
        {m.text && <div className="agent-msg me">{m.text}</div>}
      </div>
    );
  const cards = m.proposalIds.map((id) => proposals.get(id)).filter((p): p is AgentProposalDto => !!p && !skip.has(p.id));
  return (
    <div className="agent-msg ai">
      {m.fallback ? <p className="agent-notice">{t(m.fallback === 'limit_month' ? 'agent.limitMonth' : m.fallback === 'limit' ? 'agent.limit' : 'agent.unavailable')}</p> : m.text ? <Markdown text={shown} /> : null}
      {m.question && <p className="agent-q">{m.question.text}</p>}
      {cards.map((p) => <ProposalCard key={p.id} p={p} disabled={busy} />)}
      {!m.fallback && m.text && !busy && <Feedback m={m} />}
    </div>
  );
}

/** Daumen hoch/runter unter einer Antwort (docs/17 §Rückmeldung); bei „runter“ optional ein Satz, was falsch war. */
function Feedback({ m }: { m: AgentMessageDto }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useNotify();
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const send = async (rating: AgentFeedbackRating, text?: string) => {
    try {
      await api.agent.feedback(m.id, { rating, ...(text ? { comment: text } : {}) });
      qc.setQueryData<AgentThread>(qk.agent.thread, (old) => (old ? { ...old, messages: old.messages.map((x) => (x.id === m.id ? { ...x, feedback: rating } : x)) } : old));
      if (rating === 'down' && !text) setOpen(true);
      else {
        setOpen(false);
        toast.ok(t('agent.feedback.thanks'));
      }
    } catch (e) {
      toast.err(e);
    }
  };
  return (
    <div className="agent-feedback">
      <IconButton icon="thumbUp" size="sm" label={t('agent.feedback.up')} aria-pressed={m.feedback === 'up'} className={m.feedback === 'up' ? 'on' : undefined} onClick={() => void send('up')} />
      <IconButton icon="thumbDown" size="sm" label={t('agent.feedback.down')} aria-pressed={m.feedback === 'down'} className={m.feedback === 'down' ? 'on' : undefined} onClick={() => void send('down')} />
      {open && (
        <form className="agent-feedback-form" onSubmit={(e) => { e.preventDefault(); void send('down', comment.trim()); }}>
          <label className="small" htmlFor={`fb-${m.id}`}>{t('agent.feedback.commentLabel')}</label>
          <input id={`fb-${m.id}`} className="input" maxLength={1000} value={comment} placeholder={t('agent.feedback.commentPlaceholder')} onChange={(e) => setComment(e.target.value)} />
          <Button size="sm" type="submit" variant="secondary">{t('agent.feedback.send')}</Button>
        </form>
      )}
    </div>
  );
}

const TIP_ICON: Record<AgentTip['severity'], IconName> = { overdue: 'alert', soon: 'deadlines', info: 'info', hint: 'info' };

function Tip({ tip, ready, onAsk, onHide }: { tip: AgentTip; ready: boolean; onAsk: (q: string) => void; onHide: () => void }) {
  const { t } = useTranslation();
  const base = `agent.${tip.key.startsWith('page.') ? tip.key : `tip.${tip.key}`}`;
  const hide = <IconButton icon="close" label={t('agent.hideTip')} size="sm" className="agent-x" onClick={onHide} />;
  if (tip.key.startsWith('page.')) {
    return (
      <div className="agent-tip hint">
        <div className="agent-tip-main">
          <p>{t(`${base}.body`)}</p>
          <div className="agent-chips">
            {(['chip1', 'chip2'] as const).map((c) => (
              <button key={c} type="button" className="agent-qchip" disabled={!ready} onClick={() => onAsk(t(`${base}.${c}`))}>{t(`${base}.${c}`)}</button>
            ))}
          </div>
        </div>
        {hide}
      </div>
    );
  }
  return (
    <div className={`agent-tip ${tip.severity}`}>
      <span className="agent-tip-icon" aria-hidden><Icon name={TIP_ICON[tip.severity]} size={15} /></span>
      <div className="agent-tip-main">
        <div className="agent-tip-text">
          <strong>{t(`${base}.title`, tip.params)}</strong>
          <span>{t(`${base}.body`, tip.params)}</span>
        </div>
        {(tip.prompt || tip.href) && (
          <div className="agent-tip-actions">
            {tip.prompt && <button type="button" className="agent-qchip sm" disabled={!ready} onClick={() => onAsk(t(`${base}.prompt`, tip.params))}>{t('agent.askMe')}</button>}
            {tip.href && <Link className="agent-tip-open" to={tip.href}>{t('agent.openLink')}<Icon name="chevronRight" size={14} /></Link>}
          </div>
        )}
      </div>
      {hide}
    </div>
  );
}
